import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { Tenant, Zone } from '@irrigation/core';
import {
  api,
  type FlumeCredentialsInput,
  type FlumeDeviceSummary,
  type RachioDeviceSummary,
  type RachioZoneSummary,
} from '../api.js';
import {
  Banner,
  Button,
  Card,
  CardSkeleton,
  EmptyState,
  Field,
  Pill,
  TextInput,
  useToast,
} from '../components/ui.js';
import { errorMessage } from '../lib/format.js';
import { useLoad } from '../lib/useLoad.js';

interface ConnectData {
  tenant: Tenant;
  zones: Zone[];
  /** Zones on the Rachio device, fetched with the stored token when connected. */
  discovered: RachioZoneSummary[];
  discoverError: string;
}

async function loadConnectData(): Promise<ConnectData> {
  const [tenant, zones] = await Promise.all([api.getMe(), api.listZones()]);
  let discovered: RachioZoneSummary[] = [];
  let discoverError = '';
  if (tenant.rachio.status === 'CONNECTED') {
    try {
      discovered = await api.listRachioZones();
    } catch (e) {
      discoverError = errorMessage(e, 'Could not fetch zones from Rachio.');
    }
  }
  return { tenant, zones, discovered, discoverError };
}

export function Connect() {
  const { data, loading, error, reload } = useLoad(loadConnectData);

  if (loading) return <CardSkeleton lines={3} />;
  if (error || !data) return <Banner tone="error">{error || 'Failed to load.'}</Banner>;

  const conn = data.tenant.rachio;
  const connected = conn.status === 'CONNECTED';

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Rachio connection</h1>
          <div className="sub">Your controller only opens valves — all scheduling decisions happen here.</div>
        </div>
        {connected ? (
          <Pill tone="good">Connected — {conn.deviceName ?? 'device'}</Pill>
        ) : conn.status === 'FAILED' ? (
          <Pill tone="bad">Connection failed</Pill>
        ) : (
          <Pill tone="muted">Not connected</Pill>
        )}
      </div>

      {conn.status === 'FAILED' && (
        <Banner tone="error">
          {/^.*\b401\b.*$/.test(conn.error ?? '')
            ? 'Your Rachio token stopped working — most likely it was revoked, or regenerated in the Rachio app. Get a fresh key at app.rach.io → Account Settings → Get API Key and paste it below.'
            : (conn.error ?? 'The connection to your controller failed. Re-enter your Rachio API token below.')}
        </Banner>
      )}

      {/* Order matters: the token card stays put and the zone list appears
          BELOW it. Mounting the picker above pushed content over the button the
          user had just clicked, while that button relabelled itself to
          "Re-connect", at the exact moment the flow should feel like progress. */}
      <TokenCard connected={connected} failed={conn.status === 'FAILED'} onConnected={() => void reload()} />

      {connected && (
        <ZonePicker
          discovered={data.discovered}
          discoverError={data.discoverError}
          existing={data.zones}
          onImported={() => void reload()}
          onRefresh={() => void reload()}
        />
      )}

      {/* Below the zone list on purpose: the meter is optional and only starts
          paying off once zones exist and have watered a few times. */}
      <FlumeCard tenant={data.tenant} onChanged={() => void reload()} />
    </div>
  );
}

/**
 * Connect an optional Flume water meter.
 *
 * Four inputs rather than one token, because Flume's personal API uses an OAuth2
 * password grant: two values from their developer portal and the tenant's own
 * Flume login. That is a lot to ask for, so the copy is explicit about what it
 * buys (measured water, broken-head alerts, proof a valve really opened) and
 * about the fact that none of it comes back to the browser.
 */
function FlumeCard({ tenant, onChanged }: { tenant: Tenant; onChanged: () => void }) {
  const conn = tenant.flume;
  const connected = conn?.status === 'CONNECTED';

  const [creds, setCreds] = useState<FlumeCredentialsInput>({
    clientId: '',
    clientSecret: '',
    username: '',
    password: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [devices, setDevices] = useState<FlumeDeviceSummary[]>([]);
  const [open, setOpen] = useState(false);
  const toast = useToast();

  const set = (key: keyof FlumeCredentialsInput) => (e: { target: { value: string } }) =>
    setCreds((c) => ({ ...c, [key]: e.target.value }));

  const complete = Object.values(creds).every((v) => v.trim().length > 0);

  const connect = async (deviceId?: string) => {
    setBusy(true);
    setError('');
    try {
      const res = await api.connectFlume(
        {
          clientId: creds.clientId.trim(),
          clientSecret: creds.clientSecret.trim(),
          username: creds.username.trim(),
          password: creds.password,
        },
        deviceId,
      );
      if (res.connection.status === 'CONNECTED') {
        toast(`Water meter connected — ${res.connection.deviceName ?? 'meter'}.`);
        setCreds({ clientId: '', clientSecret: '', username: '', password: '' });
        setDevices([]);
        setOpen(false);
        onChanged();
      } else if (res.devices.length > 1) {
        setDevices(res.devices);
      } else {
        setError(res.connection.error || 'Could not connect. Check the details and try again.');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not connect to Flume.'));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.disconnectFlume();
      toast('Water meter disconnected.');
      onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not disconnect.'));
    } finally {
      setBusy(false);
    }
  };

  if (devices.length > 1) {
    return (
      <Card title="Choose a meter" sub="This Flume account has several water sensors — pick the one on this property.">
        <div className="stack">
          {error && <Banner tone="error">{error}</Banner>}
          <div className="list">
            {devices.map((d) => (
              <div className="run-row" key={d.id}>
                <div className="grow">
                  <div className="title">{d.label}</div>
                  <div className="meta">
                    {d.connected ? 'Online' : 'Offline'}
                    {d.batteryLevel ? ` · battery ${d.batteryLevel}` : ''}
                  </div>
                </div>
                <Button variant="subtle" size="sm" busy={busy} onClick={() => void connect(d.id)}>
                  Use this one
                </Button>
              </div>
            ))}
          </div>
          <div className="actions">
            {/* Keep the typed credentials, as the Rachio picker does: they just
                validated, and re-typing four secrets to reconsider a two-option
                choice is a miserable way to spend a minute. */}
            <Button variant="ghost" size="sm" onClick={() => setDevices([])}>
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  if (connected && !open) {
    return (
      <Card
        title="Water meter"
        sub="Measured water usage, broken-head alerts, and confirmation that a valve really opened."
      >
        <div className="stack">
          <div className="run-row">
            <div className="grow">
              <div className="title">{conn?.deviceName ?? 'Flume meter'}</div>
              <div className="meta">
                Connected{conn?.lastSyncAt ? ` · last reading ${new Date(conn.lastSyncAt).toLocaleString()}` : ''}
              </div>
            </div>
            <Pill tone="good">Connected</Pill>
          </div>
          {conn?.irrigationUnmetered && (
            <Banner tone="info">
              This meter does not appear to see your irrigation — every zone measured no water. That usually means
              irrigation runs off a well, a pump, or a separate meter upstream of the Flume. Flow alerts stay off
              while that is the case.
            </Banner>
          )}
          <div className="actions">
            <Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
              Reconnect
            </Button>
            <Button variant="ghost" size="sm" busy={busy} onClick={() => void disconnect()}>
              Disconnect
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={connected ? 'Reconnect your water meter' : 'Connect a water meter (optional)'}
      sub={
        connected
          ? 'Only needed if you regenerated your Flume API credentials or changed your password.'
          : 'If you have a Flume meter, it lets us measure the water each zone actually used — and tell you when a zone starts using more than usual.'
      }
    >
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void connect();
        }}
      >
        {error && <Banner tone="error">{error}</Banner>}
        {conn?.status === 'FAILED' && !error && <Banner tone="error">{conn.error}</Banner>}

        <Banner tone="info">
          Flume&apos;s API needs both an API client and your Flume login. Generate the client at{' '}
          <a href="https://portal.flumetech.com/settings" target="_blank" rel="noreferrer">
            portal.flumetech.com
          </a>{' '}
          → Settings → Generate API Client. All four values are validated, then stored encrypted — none of them is
          ever shown again or sent back to this browser.
        </Banner>

        <Field label="Client ID">
          {(id) => (
            <TextInput
              id={id}
              className="mono"
              type="password"
              autoComplete="off"
              value={creds.clientId}
              onChange={set('clientId')}
            />
          )}
        </Field>
        <Field label="Client secret">
          {(id) => (
            <TextInput
              id={id}
              className="mono"
              type="password"
              autoComplete="off"
              value={creds.clientSecret}
              onChange={set('clientSecret')}
            />
          )}
        </Field>
        <Field label="Flume email" hint="The email you sign in to the Flume app with.">
          {(id) => (
            <TextInput
              id={id}
              type="email"
              autoComplete="off"
              value={creds.username}
              onChange={set('username')}
            />
          )}
        </Field>
        <Field label="Flume password">
          {(id) => (
            <TextInput
              id={id}
              type="password"
              autoComplete="off"
              value={creds.password}
              onChange={set('password')}
            />
          )}
        </Field>

        <div className="actions">
          <Button type="submit" busy={busy} disabled={!complete} variant={connected ? 'ghost' : 'primary'}>
            {busy ? 'Validating…' : connected ? 'Reconnect' : 'Connect meter'}
          </Button>
          {connected && (
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          )}
        </div>
      </form>
    </Card>
  );
}

function TokenCard({
  connected,
  failed,
  onConnected,
}: {
  connected: boolean;
  failed: boolean;
  onConnected: () => void;
}) {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** Set when the account has several controllers and one must be chosen. */
  const [devices, setDevices] = useState<RachioDeviceSummary[]>([]);
  const toast = useToast();

  const connect = async (deviceId?: string) => {
    setBusy(true);
    setError('');
    try {
      const res = await api.connectRachio(token.trim(), deviceId);
      if (res.connection.status === 'CONNECTED') {
        toast(`Connected to ${res.connection.deviceName ?? 'your controller'} — found ${res.zones.length} zones.`);
        setToken('');
        setDevices([]);
        onConnected();
      } else if (res.devices.length > 1) {
        // Valid token, several controllers: ask which one to use.
        setDevices(res.devices);
      } else {
        setError(res.connection.error || 'Could not connect. Check the token and try again.');
      }
    } catch (err) {
      setError(errorMessage(err, 'Could not connect.'));
    } finally {
      setBusy(false);
    }
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void connect();
  };

  if (devices.length > 1) {
    return (
      <Card title="Choose a controller" sub="This Rachio account has several controllers — pick the one to schedule.">
        <div className="stack">
          {error && <Banner tone="error">{error}</Banner>}
          <div className="list">
            {devices.map((d) => (
              <div className="run-row" key={d.deviceId}>
                <div className="grow">
                  <div className="title">{d.name}</div>
                  <div className="meta">
                    {d.zoneCount} zones{d.model ? ` · ${d.model}` : ''}
                  </div>
                </div>
                <Button variant="subtle" size="sm" busy={busy} onClick={() => void connect(d.deviceId)}>
                  Use this one
                </Button>
              </div>
            ))}
          </div>
          <div className="actions">
            <Button
              variant="ghost"
              size="sm"
              // Keep the token: it just validated, and it's an unmemorable UUID
              // fetched from another product. Discarding it made reconsidering a
              // two-option choice cost a round trip to app.rach.io.
              onClick={() => setDevices([])}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={failed ? 'Reconnect your controller' : connected ? 'Re-connect or switch controllers' : 'Connect your controller'}
      sub={
        failed
          ? 'Paste a fresh Rachio API token to restore the connection.'
          : connected
            ? 'Only needed if your token was revoked or you replaced the controller.'
            : 'One-time setup: paste your personal Rachio API token.'
      }
    >
      <form className="stack" onSubmit={submit}>
        {error && <Banner tone="error">{error}</Banner>}
        <Field
          label="Rachio API token"
          hint={
            <>
              Find it at{' '}
              <a href="https://app.rach.io" target="_blank" rel="noreferrer">
                app.rach.io
              </a>{' '}
              → Account Settings → Get API Key. The token is validated, then stored encrypted — it is never shown
              again or sent back to this browser.
            </>
          }
        >
          {(id) => (
            <TextInput
              id={id}
              className="mono"
              type="password"
              autoComplete="off"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
          )}
        </Field>
        <div className="actions">
          <Button type="submit" busy={busy} disabled={!token.trim()} variant={connected ? 'ghost' : 'primary'}>
            {busy ? 'Validating…' : connected ? 'Re-connect' : 'Connect'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function ZonePicker({
  discovered,
  discoverError,
  existing,
  onImported,
  onRefresh,
}: {
  discovered: RachioZoneSummary[];
  discoverError: string;
  existing: Zone[];
  onImported: () => void;
  onRefresh: () => void;
}) {
  const [importedIds, setImportedIds] = useState<Set<string>>(new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [busyAll, setBusyAll] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [importNote, setImportNote] = useState<number | null>(null);
  const toast = useToast();

  const alreadyConfigured = new Set(existing.map((z) => z.rachioZoneId));
  const isImported = (z: RachioZoneSummary) => alreadyConfigured.has(z.rachioZoneId) || importedIds.has(z.rachioZoneId);
  const importable = discovered.filter((z) => !isImported(z));

  const importZone = async (z: RachioZoneSummary) => {
    await api.createZone({
      name: z.name,
      rachioZoneId: z.rachioZoneId,
      zoneNumber: z.zoneNumber,
      precipRateInPerHr: z.precipRateInPerHr,
      efficiency: z.efficiency,
      rootDepthIn: z.rootDepthIn,
      areaSqFt: z.areaSqFt,
      // Controller-configured attributes where recognizable; the rest is
      // reasonable starting agronomy the user tunes on the Zones page.
      nozzleType: z.nozzleType ?? 'FIXED_SPRAY',
      rachioNozzleName: z.nozzleName,
      soilType: z.soilType ?? 'LOAM',
      plantType: z.plantType ?? 'COOL_SEASON_GRASS',
      sunExposure: z.sunExposure ?? 'FULL_SUN',
      slope: z.slope ?? 'FLAT',
    });
    setImportedIds((s) => new Set(s).add(z.rachioZoneId));
    setImportNote((n) => (n ?? 0) + 1);
  };

  const importOne = async (z: RachioZoneSummary) => {
    setBusyId(z.rachioZoneId);
    try {
      await importZone(z);
      toast(`Imported "${z.name}".`);
      onImported();
    } catch (e) {
      toast(errorMessage(e, `Failed to import "${z.name}".`), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const importAll = async () => {
    setBusyAll(true);
    setProgress({ done: 0, total: importable.length });
    // Parallel: this was one serial round-trip per zone, so a 16-zone
    // controller meant 5-15 seconds behind a button that looked frozen.
    const results = await Promise.allSettled(
      importable.map((z) =>
        importZone(z).then(
          () => {
            setProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
            return z;
          },
          (err) => {
            setProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
            throw Object.assign(err instanceof Error ? err : new Error(String(err)), { zoneName: z.name });
          },
        ),
      ),
    );
    const failedNames = results
      .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      .map((r) => (r.reason as { zoneName?: string }).zoneName ?? 'a zone');
    const ok = results.length - failedNames.length;
    setBusyAll(false);
    setProgress(null);
    onImported();
    // Whenever anything imported, say so — those zones carry guessed agronomy
    // whether or not their siblings failed.
    if (ok > 0) setImportNote(ok);
    if (failedNames.length === 0) toast(`Imported ${ok} zones.`);
    else toast(`Imported ${ok} zones. Failed: ${failedNames.join(', ')} — try again.`, 'error');
  };

  if (discoverError) {
    return (
      <Card title="Zones on your controller">
        <div className="stack">
          <Banner tone="error">{discoverError}</Banner>
          <div className="actions">
            <Button variant="ghost" size="sm" onClick={onRefresh}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Zones on your controller"
      sub="Import the zones you want this scheduler to manage. Zones added in the Rachio app show up here automatically."
      right={
        importable.length > 1 ? (
          <Button variant="subtle" size="sm" busy={busyAll} onClick={() => void importAll()}>
            {progress ? `Importing ${progress.done} of ${progress.total}…` : `Import all (${importable.length})`}
          </Button>
        ) : undefined
      }
    >
      {/* A banner, not a toast: the controller doesn't report plant, soil, sun
          or slope, so those are guessed — and wrong ones change watering depth
          and frequency, which is the whole product. A 4-second toast was the
          weakest possible way to say "this data is a guess". */}
      {importNote !== null && (
        <Banner tone="ok">
          Imported {importNote} zone{importNote === 1 ? '' : 's'}. Your controller doesn't report plant, soil, sun or
          slope, so those were set to sensible defaults — review them on the <Link to="/zones">Zones page</Link>, since
          they drive how much water each zone gets.
        </Banner>
      )}

      {discovered.length === 0 ? (
        <EmptyState icon="🚿" title="No zones on this device">
          Set up zones in the Rachio app first, then import them here.
        </EmptyState>
      ) : (
        <div className="list">
          {discovered.map((z) => {
            const done = isImported(z);
            return (
              <div className="run-row" key={z.rachioZoneId}>
                <div className="grow">
                  <div className="title">
                    #{z.zoneNumber} {z.name}
                    {!z.enabled && (
                      <Pill tone="muted" plain>
                        Disabled in Rachio
                      </Pill>
                    )}
                  </div>
                  {z.precipRateInPerHr !== undefined && (
                    <div className="meta">{z.precipRateInPerHr} in/hr from Rachio</div>
                  )}
                </div>
                {done ? (
                  <Pill tone="good">Imported</Pill>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    busy={busyId === z.rachioZoneId}
                    disabled={busyAll}
                    onClick={() => void importOne(z)}
                  >
                    Import
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
